import DesktopAuthWrapper from '@components/atoms/DesktopAuthWrapper';
import { useVerify } from '@hooks/api';
import { hasErrorCode, ErrorCode } from '@models/api/errors';
import { useDebounce } from '@hooks/useDebounce';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { useReauthParamsURL } from '@hooks/useReauthParams';
import { useRedirect } from '@hooks/useRedirect';
import { TOTPInput } from '@moreharvest/component-library';
import { authErrorCode, login, reauth } from '@services/analytics';
import {
    AnalyticsAuthErrorCode,
    AnalyticsAuthMethod,
    AnalyticsAuthStage,
    SecretType,
    VerifyAuthRequest,
} from 'mh-client';
import { useAppSelector } from '@store/index';
import { selectEmail } from '@store/reducers/profile';
import { RE_DIGIT } from '@utils/constants';
import { FormattedMessage } from '@utils/i18n';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

const LoginWithTOTP = () => {
    const navigate = useNavigate();
    const { mutateAsync: verify, isPending } = useVerify();
    const [code, setCode] = useState('');
    const debouncedCode = useDebounce(code, 1000);
    const email = useAppSelector(selectEmail);
    const { url, scopes, isReauth } = useReauthParamsURL();
    const fireAuth = isReauth ? reauth : login;
    const CODE_LENGTH = 6;
    const intl = useIntl();
    const [errorMessage, setErrorMessage] = useState('');
    useRedirect(email.length > 0, '/login');

    const initiatedFiredRef = useRef(false);
    const handleFieldFocus = () => {
        if (initiatedFiredRef.current) return;
        initiatedFiredRef.current = true;
        fireAuth({
            method: AnalyticsAuthMethod.Totp,
            stage: AnalyticsAuthStage.Initiated,
        });
    };

    const verifyCode = async (totpCode: string) => {
        try {
            await verify(
                new VerifyAuthRequest(email, SecretType.totp, totpCode, scopes)
            );
            fireAuth({
                method: AnalyticsAuthMethod.Totp,
                stage: AnalyticsAuthStage.Success,
            });
            navigate(url);
        } catch (error: unknown) {
            fireAuth({
                method: AnalyticsAuthMethod.Totp,
                stage: AnalyticsAuthStage.Failed,
                error_code: authErrorCode(
                    error,
                    AnalyticsAuthErrorCode.InvalidTotp
                ),
            });
            if (hasErrorCode(error, ErrorCode.InvalidTotpCode)) {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login_totp_incorrect_2fa_code',
                    })
                );
                setCode('');
            } else {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'oops_something_went_wrong',
                    })
                );
            }
        }
    };

    useLoadingModal(isPending);

    useEffect(() => {
        if (
            debouncedCode.trim().length === CODE_LENGTH &&
            RE_DIGIT.test(debouncedCode)
        ) {
            verifyCode(debouncedCode.trim());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedCode]);

    return (
        <DesktopAuthWrapper>
            <h4 className="H4 semibold text-center">
                <FormattedMessage id="totp_enter_code_from_auth_app" />
            </h4>
            <p className="Body-Small text-center">
                <FormattedMessage id="login_totp_enter_code_info" />
                <FormattedMessage
                    id="security_security_info"
                    values={{
                        a: chunks => (
                            <a
                                href="https://support.moreharvest.com/hc/en-us/categories/10586151671183-Account-and-login"
                                className="underline hover:no-underline"
                                target="_blank"
                            >
                                {chunks}
                            </a>
                        ),
                    }}
                />
            </p>
            <div className="contents" onFocus={handleFieldFocus}>
                <TOTPInput
                    code={code}
                    codeLength={CODE_LENGTH}
                    onChange={newValue => {
                        if (errorMessage) {
                            setErrorMessage('');
                        }
                        setCode(newValue);
                    }}
                    errorMessage={errorMessage}
                />
            </div>
        </DesktopAuthWrapper>
    );
};

export default LoginWithTOTP;
